import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsDateString,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../../../../@base/base.dto';
import { AgentProfileId, AppointmentId, PropertyApplicationId, PropertyId } from '../../../../types';
import type { AgentProfileDto } from '../agent-profile/agent-profile.dto';
import type { PropertyDto } from '../property/property.dto';

export const PropertyApplicationFilterKey = {
  Status: 'status',
} as const;
export type PropertyApplicationFilterKey =
  (typeof PropertyApplicationFilterKey)[keyof typeof PropertyApplicationFilterKey];
export const PROPERTY_APPLICATION_FILTER_KEYS = [PropertyApplicationFilterKey.Status] as const;

export enum PropertyApplicationStatus {
  PENDING = 'pending',
  VISIT_INVITED = 'visit_invited',
  VISIT_CONFIRMED = 'visit_confirmed',
  VISITED = 'visited',
  OFFER_SUBMITTED = 'offer_submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

export enum PropertyApplicationPendingActionType {
  VISIT_PROPOSAL = 'visit_proposal',
  VISIT_RESCHEDULE = 'visit_reschedule',
  FINAL_OFFER = 'final_offer',
  MANDATE_SELECTION = 'mandate_selection',
}

export class PropertyApplicationPendingActionDto {
  @IsDateString()
  @IsOptional()
  deadline?: Date | string;

  @IsString()
  @IsNotEmpty()
  waitingFor!: 'agent' | 'owner';

  @IsEnum(PropertyApplicationPendingActionType)
  @IsNotEmpty()
  actionType!: PropertyApplicationPendingActionType;

  @IsBoolean()
  @IsOptional()
  warningSent?: boolean;
}

export enum PropertyApplicationService {
  PROFESSIONAL_PHOTOS = 'professional_photos',
  VIRTUAL_TOUR_3D = 'virtual_tour_3d',
  DRONE = 'drone',
  HOME_STAGING = 'home_staging',
  PREMIUM_PORTALS = 'premium_portals',
  SOCIAL_MEDIA_ADS = 'social_media_ads',
  FINANCIAL_SCREENING = 'financial_screening',
  ACTIVE_BUYER_POOL = 'active_buyer_pool',
  REGULAR_REPORTING = 'regular_reporting',
}

export class PropertyApplicationValuationDto {
  @IsNumber()
  @Min(0)
  @IsOptional()
  attractivePrice?: number;

  @IsNumber()
  @Min(0)
  @IsOptional()
  recommendedPrice?: number;

  @IsNumber()
  @Min(0)
  @IsOptional()
  heartStrokePrice?: number;

  @IsString()
  @IsOptional()
  generalPresentation?: string;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  strengths?: string[];

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  weaknesses?: string[];
}

export class PropertyApplicationEngagementDto {
  @IsNumber()
  @Min(0)
  @Max(25)
  @IsNotEmpty()
  commissionRate!: number;

  @IsNumber()
  @Min(0)
  @Max(25)
  @IsOptional()
  initialCommissionRate?: number;

  @IsNumber()
  @Min(0)
  @Max(25)
  @IsOptional()
  finalCommissionRate?: number;

  @IsArray()
  @IsEnum(PropertyApplicationService, { each: true })
  @IsOptional()
  services?: PropertyApplicationService[];

  @ValidateNested()
  @Type(() => PropertyApplicationValuationDto)
  @IsOptional()
  valuation?: PropertyApplicationValuationDto;

  @IsDate()
  @Type(() => Date)
  @IsOptional()
  finalOfferSubmittedAt?: Date;
}

export class PropertyApplicationDto extends BaseDto<PropertyApplicationId> {
  @IsNotEmpty()
  property!: PropertyId | PropertyDto;

  @IsNotEmpty()
  agent!: AgentProfileId | AgentProfileDto;

  @IsEnum(PropertyApplicationStatus)
  @IsNotEmpty()
  status!: PropertyApplicationStatus;

  @ValidateNested()
  @Type(() => PropertyApplicationEngagementDto)
  @IsNotEmpty()
  engagement!: PropertyApplicationEngagementDto;

  @IsNumber()
  @Min(0)
  @IsNotEmpty()
  creditsConsumed!: number;

  @IsString()
  @IsOptional()
  discussion?: string;

  @IsString()
  @IsOptional()
  appointment?: AppointmentId;

  @ValidateNested()
  @Type(() => PropertyApplicationPendingActionDto)
  @IsOptional()
  pendingAction?: PropertyApplicationPendingActionDto;
}

export class PropertyApplicationCreationDto {
  @IsString()
  @IsNotEmpty()
  propertyId!: PropertyId;

  @ValidateNested()
  @Type(() => PropertyApplicationEngagementDto)
  @IsNotEmpty()
  engagement!: PropertyApplicationEngagementDto;

  @IsString()
  @IsNotEmpty()
  message!: string;
}

export class PropertyApplicationWithdrawDto {
  @IsString()
  @IsOptional()
  reason?: string;
}
